import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { FiMenu, FiX, FiUser, FiLogOut, FiGrid, FiChevronDown, FiSun, FiMoon, FiGlobe } from 'react-icons/fi';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'id' ? 'en' : 'id';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => { setMobileOpen(false); }, [location]);

  const handleLogout = () => { logout(); navigate('/'); setDropdownOpen(false); };
  const isActive = (path) => location.pathname === path ? 'active' : '';

  const isDashboard = location.pathname.startsWith('/dashboard');
  if (isDashboard) return null;

  return (
    <>
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <motion.div
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px',
            background: 'var(--primary-500)', transformOrigin: '0%', scaleX
          }}
        />
        <div className="navbar-inner">
          <Link to="/" className="navbar-brand">
            <motion.div 
              className="brand-icon"
              whileHover={{ rotate: 10, scale: 1.1 }}
            >
              N
            </motion.div>
            <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
            >
              NGADHU
              <span className="brand-sub">Satu Data Kabupaten Ngada</span>
            </motion.div>
          </Link>

          <ul className="nav-links">
            <li><Link to="/" className={isActive('/')}>{t('nav.home')}</Link></li>
            <li><Link to="/dataset" className={isActive('/dataset')}>{t('nav.dataset')}</Link></li>
            <li><Link to="/analitik" className={isActive('/analitik')}>{t('nav.analitik')}</Link></li>
            <li><Link to="/peta" className={isActive('/peta')}>{t('nav.map')}</Link></li>
            <li><Link to="/berita" className={isActive('/berita')}>{t('nav.news')}</Link></li>
            <li><Link to="/profil" className={isActive('/profil')}>{t('nav.profile')}</Link></li>
          </ul>

          <div className="nav-auth">
            {user ? (
              <div className="dropdown" ref={dropdownRef}>
                <button className="btn btn-ghost" onClick={() => setDropdownOpen(!dropdownOpen)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div className="user-avatar">{user.nama?.charAt(0)?.toUpperCase()}</div>
                  <span style={{ fontSize: '0.85rem' }}>{user.nama?.split(' ')[0]}</span>
                  <FiChevronDown size={14} />
                </button>
                {dropdownOpen && (
                  <div className="dropdown-menu">
                    <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--gray-100)' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{user.nama}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', textTransform: 'capitalize' }}>{user.role}</div>
                    </div>
                    <Link to="/dashboard" className="dropdown-item" onClick={() => setDropdownOpen(false)}>
                      <FiGrid size={15} /> {t('nav.dashboard')}
                    </Link>
                    <Link to="/dashboard/profil" className="dropdown-item" onClick={() => setDropdownOpen(false)}>
                      <FiUser size={15} /> {t('nav.my_profile')}
                    </Link>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={handleLogout} style={{ color: 'var(--danger)' }}>
                      <FiLogOut size={15} /> {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-sm">{t('nav.login')}</Link>
                <Link to="/register" className="btn btn-primary btn-sm">{t('nav.register')}</Link>
              </>
            )}
            <div style={{ width: '1px', height: '24px', background: 'var(--gray-200)', margin: '0 0.5rem' }} />
            
            <button className="btn btn-icon btn-ghost" onClick={toggleLanguage} title={i18n.language === 'id' ? 'English' : 'Indonesia'} style={{ position: 'relative' }}>
              <FiGlobe size={18} />
              <span style={{ position: 'absolute', bottom: '2px', right: '2px', fontSize: '10px', fontWeight: 'bold' }}>{i18n.language.toUpperCase()}</span>
            </button>

            <button className="btn btn-icon btn-ghost" onClick={toggleTheme} title={`Ganti ke mode ${theme === 'light' ? 'gelap' : 'terang'}`}>
              {theme === 'light' ? <FiMoon size={18} /> : <FiSun size={18} />}
            </button>
            <button className="hamburger" onClick={() => setMobileOpen(true)}>
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="mobile-nav" onClick={() => setMobileOpen(false)}>
          <div className="mobile-nav-inner" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <span style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--primary-700)' }}>NGADHU</span>
              <button onClick={() => setMobileOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={22} /></button>
            </div>
            <ul className="nav-links">
              <li><Link to="/" className={isActive('/')}>{t('nav.home')}</Link></li>
              <li><Link to="/dataset" className={isActive('/dataset')}>{t('nav.dataset')}</Link></li>
              <li><Link to="/analitik" className={isActive('/analitik')}>{t('nav.analitik')}</Link></li>
              <li><Link to="/peta" className={isActive('/peta')}>{t('nav.map')}</Link></li>
              <li><Link to="/berita" className={isActive('/berita')}>{t('nav.news')}</Link></li>
              <li><Link to="/profil" className={isActive('/profil')}>{t('nav.profile')}</Link></li>
              {user ? (
                <>
                  <li><Link to="/dashboard">{t('nav.dashboard')}</Link></li>
                  <li><button onClick={handleLogout} style={{ color: 'var(--danger)' }}>{t('nav.logout')}</button></li>
                </>
              ) : (
                <>
                  <li><Link to="/login">{t('nav.login')}</Link></li>
                  <li><Link to="/register">{t('nav.register')}</Link></li>
                </>
              )}
            </ul>
            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={toggleLanguage} style={{ flex: 1 }}>
                <FiGlobe size={16} /> {i18n.language === 'id' ? 'English' : 'Indonesia'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
